import * as E from 'fp-ts/lib/Either';
import * as t from 'io-ts';
import {reporter} from 'io-ts-reporters';
import _, {first} from 'lodash';

import {ErrorWithData} from '../../libs/shared/core/src/helpers/types/customErrors';
import {reportToBugsnag} from '../../libs/shared/core/src/services/bugsnag/bugsnag';
import {ConversationsTask, RConversationsTask} from './conversations/conversationsTask';
import {RSendCommentTask} from './sendCommentTask';
import {RSendMessageTask} from './sendMessageTask';
import {PrimitiveTask} from './task';
import {taskStore} from './taskStore';

/*
 * Types.
 */

export type AppTask =
  | ConversationsTask
  | t.TypeOf<typeof RSendMessageTask>
  | t.TypeOf<typeof RSendCommentTask>;

/*
 * Runtime types.
 */

export const RAppTask = t.taggedUnion('name', [RConversationsTask, RSendMessageTask, RSendCommentTask]);

/*
 * Helpers.
 */

export async function tryLoadAppTasksAsync(userId: number): Promise<ReadonlyArray<AppTask>> {
  const primitiveTasks = await taskStore.tryLoadTasksAsync(userId);

  return _(primitiveTasks).map(decodeAppTask).compact().value();
}

export function saveAppTaskAsync(userId: number, task: AppTask): Promise<void> {
  const primitiveTask = encodeAppTask(task);
  return taskStore.saveTaskAsync(userId, primitiveTask);
}

export function encodeAppTask(task: AppTask): PrimitiveTask<string, unknown> {
  return RAppTask.encode(task);
}

export function decodeAppTask(input: unknown): AppTask | undefined {
  const result = RAppTask.decode(input);

  if (!E.isRight(result)) {
    const decodeError = first(reporter(result));
    reportToBugsnag(
      new ErrorWithData(`[io-ts] Error parsing app task`, {
        decodeError,
        rawModel: JSON.stringify(input, null, 2).slice(0, 1024),
      }),
    );
    return undefined;
  }

  return result.right;
}
